// textarea のキャレット座標を取得するユーティリティ
//
// 仕組み: mirror div 方式
//   textarea と同じ font / padding / line-height / width / word-break を持つ
//   非表示 <div> を body に用意し、`value.slice(0, selectionStart)` を入れた末尾に
//   マーカー <span> を配置する。その span の offsetTop/offsetLeft を読めば、
//   textarea のテキスト座標系でのキャレット位置が取れる。
//
//   取得した相対座標に textarea.getBoundingClientRect() を足すことで
//   viewport 座標、textarea 内部座標系（= overlay の座標系）への変換は
//   呼び出し側で行う。
//
// 参考: koddsson/textarea-caret-position の実装アプローチ。
//   依存追加を避けて自前で最小実装する（Element UI v2 + Vue 2 の古い環境のため）。

// コピーするプロパティ一覧（textarea のメトリクス完全再現に必要なもの）
const MIRROR_PROPS = [
  "boxSizing",
  "width",
  "height",
  "overflowX",
  "overflowY",

  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderStyle",

  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",

  "fontStyle",
  "fontVariant",
  "fontWeight",
  "fontStretch",
  "fontSize",
  "fontSizeAdjust",
  "lineHeight",
  "fontFamily",

  "textAlign",
  "textTransform",
  "textIndent",
  "textDecoration",

  "letterSpacing",
  "wordSpacing",

  "tabSize",
  "MozTabSize",

  "whiteSpace",
  "wordBreak",
  "wordWrap",
  "overflowWrap",
];

// 使い回しの mirror div（モジュールスコープで単一インスタンス）
let sharedMirror = null;

function getMirror() {
  if (sharedMirror && document.body.contains(sharedMirror)) return sharedMirror;
  const div = document.createElement("div");
  div.setAttribute("aria-hidden", "true");
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.top = "0";
  div.style.left = "-9999px";
  div.style.pointerEvents = "none";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordBreak = "break-word";
  document.body.appendChild(div);
  sharedMirror = div;
  return div;
}

/**
 * textarea のキャレット位置（selectionEnd 相当）の座標を返す。
 * 返り値はすべて textarea の「内部テキスト座標系」— つまり
 * textarea のスクロールが 0 のときに textarea.offsetLeft/offsetTop からの
 * 相対位置で、キャレット行の先頭(x)・ベースライン手前(y)。
 *
 * @param {HTMLTextAreaElement} textarea
 * @param {number} [position]  任意: 測定したい文字インデックス。省略時は selectionEnd
 * @returns {{ top: number, left: number, height: number }}
 */
export function getCaretCoordinates(textarea, position) {
  if (!textarea) return { top: 0, left: 0, height: 0 };
  const pos = typeof position === "number" ? position : textarea.selectionEnd;
  const mirror = getMirror();
  const cs = window.getComputedStyle(textarea);

  // スタイルをコピー
  for (const prop of MIRROR_PROPS) {
    try {
      mirror.style[prop] = cs[prop];
    } catch (e) {
      /* 一部プロパティは setter なしのブラウザあり */
    }
  }

  // width は border-box / content-box 差異を吸収
  // → textarea の clientWidth（padding込み・border抜き）に合わせ、padding は
  //   既に上で設定済みなのでそのままで OK
  mirror.style.width = `${textarea.clientWidth}px`;
  mirror.style.height = "auto";
  mirror.style.minHeight = `${textarea.clientHeight}px`;

  const value = textarea.value || "";
  const before = value.substring(0, pos);

  // テキストはそのまま入れる。末尾改行を考慮して、marker span に zero-width space
  // を入れて高さを確保する。
  mirror.textContent = before;
  const span = document.createElement("span");
  span.textContent = value.substring(pos) || "."; // 空だと高さ 0 になる
  // marker 以降は測定に関係ないが、キャレット位置の span として可視化対象
  mirror.appendChild(span);

  const top = span.offsetTop;
  const left = span.offsetLeft;
  // キャレット行の高さは computed line-height を使う。数値化できないときは fontSize の 1.2 倍で代用
  let lineHeight = parseFloat(cs.lineHeight);
  if (isNaN(lineHeight)) {
    lineHeight = parseFloat(cs.fontSize) * 1.2;
  }

  // 測定後、内容を空にしておく（メモリ節約）
  mirror.textContent = "";

  return {
    top,
    left,
    height: lineHeight,
  };
}

/**
 * 測定用 mirror div を破棄する（テスト／ホットリロード用）。
 * 通常運用では呼ばなくてよい。
 */
export function destroyMirror() {
  if (sharedMirror && sharedMirror.parentNode) {
    sharedMirror.parentNode.removeChild(sharedMirror);
  }
  sharedMirror = null;
}
